import React, { useMemo, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import FormatBold from "@mui/icons-material/FormatBold";
import FormatItalic from "@mui/icons-material/FormatItalic";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListSubheader,
  Typography,
} from "@mui/material";
import { markdownToEditorContent, serializeEditorDoc } from "../../Utils/workMarkdown";

const toolbarButtonSx = (active) => ({
  minWidth: 0,
  px: 1,
  color: active ? "#3598b7" : "#2e2e2e",
  fontWeight: active ? 700 : 500,
});

const WorkBodyEditor = ({
  locale = "en",
  catalog = [],
  selectedEquipmentIds = [],
  onChange,
  placeholder,
  initialMarkdown = "",
  catalogReady = false,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [, setEditorTick] = useState(0);
  const labelKey = locale === "no" ? "no" : "en";
  const serviceKey = locale === "no" ? "serviceNo" : "serviceEn";

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        strike: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        horizontalRule: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: false,
        HTMLAttributes: { rel: "noopener noreferrer" },
      }),
    ],
    immediatelyRender: false,
    content: markdownToEditorContent(initialMarkdown),
    onUpdate: ({ editor: current }) => {
      setEditorTick((tick) => tick + 1);
      if (typeof onChange === "function") {
        onChange(serializeEditorDoc(current.getJSON()));
      }
    },
    onSelectionUpdate: () => setEditorTick((tick) => tick + 1),
  });

  const groupedCatalog = useMemo(() => {
    const selected = new Set(selectedEquipmentIds);
    const selectedItems = catalog.filter((item) => selected.has(item.id));
    const remaining = catalog.filter((item) => !selected.has(item.id));
    const groups = [];
    if (selectedItems.length > 0) {
      groups.push({
        id: "selected",
        label: "Selected equipment",
        items: selectedItems,
      });
    }
    const byService = remaining.reduce((acc, item) => {
      const key = item.serviceSlug || "other";
      if (!acc[key]) {
        acc[key] = {
          id: key,
          label: item[serviceKey] || item.serviceEn || key,
          items: [],
        };
      }
      acc[key].items.push(item);
      return acc;
    }, {});
    Object.values(byService).forEach((group) => groups.push(group));
    return groups;
  }, [catalog, selectedEquipmentIds, serviceKey]);

  const selectedText = editor?.state?.doc?.textBetween(
    editor.state.selection.from,
    editor.state.selection.to,
    " ",
  ) || "";
  const matchingItem = catalog.find((item) => {
    const label = (item[labelKey] || item.en || "").trim();
    return label && label.toLowerCase() === selectedText.trim().toLowerCase();
  });

  const insertEquipment = (item) => {
    if (!editor) return;
    const label = item[labelKey] || item.en;
    if (selectedText.trim()) {
      editor.chain().focus().setLink({ href: item.href }).run();
    } else {
      editor
        .chain()
        .focus()
        .insertContent({
          type: "text",
          text: label,
          marks: [{ type: "link", attrs: { href: item.href } }],
        })
        .run();
    }
    setMenuAnchor(null);
  };

  return (
    <Box
      sx={{
        border: "1px solid rgba(0, 0, 0, 0.23)",
        "&:hover": { borderColor: "rgba(0, 0, 0, 0.87)" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.75,
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          backgroundColor: "rgba(53, 152, 183, 0.04)",
        }}
      >
        <Button
          size="small"
          title="Main heading (##)"
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
          sx={toolbarButtonSx(editor?.isActive("heading", { level: 2 }))}
        >
          Main
        </Button>
        <Button
          size="small"
          title="Second heading (###)"
          onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
          sx={toolbarButtonSx(editor?.isActive("heading", { level: 3 }))}
        >
          Second
        </Button>
        <Button
          size="small"
          title="Bold"
          onClick={() => editor?.chain().focus().toggleBold().run()}
          sx={toolbarButtonSx(editor?.isActive("bold"))}
        >
          <FormatBold fontSize="small" />
        </Button>
        <Button
          size="small"
          title="Italic"
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          sx={toolbarButtonSx(editor?.isActive("italic"))}
        >
          <FormatItalic fontSize="small" />
        </Button>
        <Button
          size="small"
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{ minWidth: 0, color: "#3598b7" }}
        >
          Link equipment
        </Button>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          MenuListProps={{ dense: true }}
        >
          {matchingItem && (
            <MenuItem onClick={() => insertEquipment(matchingItem)}>
              Link as {matchingItem[labelKey] || matchingItem.en}
            </MenuItem>
          )}
          {groupedCatalog.map((group) => [
            <ListSubheader key={`${group.id}-head`}>
              {group.label}
            </ListSubheader>,
            ...group.items.map((item) => (
              <MenuItem key={item.id} onClick={() => insertEquipment(item)}>
                {item[labelKey] || item.en}
              </MenuItem>
            )),
          ])}
          {catalog.length === 0 && (
            <MenuItem disabled>
              {catalogReady ? "No equipment loaded" : "Equipment catalog is loading"}
            </MenuItem>
          )}
        </Menu>
      </Box>
      <Box
        aria-label={placeholder}
        sx={{
          "& .ProseMirror": {
            minHeight: 160,
            px: 1.5,
            py: 1.25,
            outline: "none",
            fontFamily: "Montserrat, sans-serif",
            fontSize: "0.95rem",
            lineHeight: 1.55,
          },
          "& .ProseMirror p": { margin: "0 0 0.75em" },
          "& .ProseMirror h2": {
            fontSize: "1.15rem",
            fontWeight: 700,
            margin: "0.55em 0 0.35em",
          },
          "& .ProseMirror h3": {
            fontSize: "1.02rem",
            fontWeight: 600,
            margin: "0.45em 0 0.3em",
          },
          "& .ProseMirror strong": { fontWeight: 700 },
          "& .ProseMirror em": { fontStyle: "italic" },
          "& .ProseMirror a": {
            color: "#3598b7",
            padding: 0,
            margin: 0,
            display: "inline",
            font: "inherit",
            letterSpacing: "inherit",
            lineHeight: "inherit",
            textDecoration: "underline",
            textUnderlineOffset: "2px",
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
      {!editor && (
        <Typography sx={{ px: 2, py: 2, color: "text.secondary" }}>
          Loading editor
        </Typography>
      )}
    </Box>
  );
};

export default WorkBodyEditor;
